/**
 * Studio → Prompt Detail (#153, W23A — 2026-05-23).
 *
 * Full-page edit surface for a tenant prompt template at
 * `/studio/prompts/:promptId`. Replaces the cramped Sheet editor for
 * everything beyond quick edits: the Sheet (mounted by `<PromptEditor>`)
 * remains for quick create + the fast-path edit; the detail page is the
 * canonical surface for typed variable authoring, live preview, and
 * "Used by Agents" lookup.
 *
 * Five tabs:
 *   - Identity       (command, title, description, category)
 *   - Template       (markdown body with helpful contextual chrome)
 *   - Variables      (typed VariableEditor, flavor="prompt")
 *   - Preview        (live {{var}} substitution against typed inputs)
 *   - Used by Agents (lists agents whose `tags` or system prompt
 *                     references this command — best-effort lookup)
 *
 * Tenant prompts come from the list endpoint (`GET /templates`) — there
 * is no per-id GET on the tenant route (only `/:command`), so the page
 * fetches the list and resolves the row client-side. This matches the
 * shared `<PromptEditor>` data path and keeps the wire contract stable.
 */

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  AlertTriangle,
  MessageSquare,
  Loader2,
  Pencil,
  Save,
  Trash2,
  X,
  Variable as VariableIcon,
  Eye,
  Users,
  Copy,
  FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  VariableEditor,
  VariableValueForm,
  previewPromptTemplate,
  variablesHaveBlankNames,
  type PromptVariableEntry,
} from '@/components/studio';
import {
  usePromptTemplates,
  useUpdatePromptTemplate,
  useDeletePromptTemplate,
  type PromptTemplate,
  type PromptVariable,
} from '@/lib/use-prompt-templates';
import { useAgents } from '@/lib/use-agents';

export const Route = createFileRoute('/studio/prompts/$promptId')({
  component: StudioPromptDetailPage,
});

type DetailTab = 'identity' | 'template' | 'variables' | 'preview' | 'used-by';

function StudioPromptDetailPage() {
  const { promptId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, error } = usePromptTemplates();
  const updatePrompt = useUpdatePromptTemplate();
  const deletePrompt = useDeletePromptTemplate();

  const prompt = useMemo(
    () => (data?.data ?? []).find((p) => p.id === promptId) ?? null,
    [data, promptId],
  );

  const [activeTab, setActiveTab] = useState<DetailTab>('identity');
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (isLoading) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
            Loading prompt…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !prompt) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <Card className="border-destructive/30">
          <CardContent className="py-6 text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{error?.message ?? 'Prompt not found.'}</div>
          </CardContent>
        </Card>
        <div className="mt-3">
          <Link
            to="/studio/prompts"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ← Back to prompts
          </Link>
        </div>
      </div>
    );
  }

  const isSystemRow = prompt.isSystem;

  const handleDelete = async () => {
    try {
      await deletePrompt.mutateAsync({ id: prompt.id });
      toast.success(`Deleted /${prompt.command}`);
      navigate({ to: '/studio/prompts' });
    } catch (err) {
      toast.error('Delete failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setConfirmDelete(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-4">
      {/* Header + back */}
      <div className="flex items-start gap-3">
        <Link
          to="/studio/prompts"
          className="text-muted-foreground hover:text-foreground p-1.5 rounded -ml-1.5"
          title="Back to prompts"
        >
          <ArrowLeft className="h-4 w-4" />
        </Link>
        <MessageSquare className="h-7 w-7 text-muted-foreground mt-0.5" />
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight truncate">
            {prompt.title}
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className="font-mono text-[11px]">
              /{prompt.command}
            </Badge>
            {prompt.category && (
              <Badge variant="outline" className="text-[10px]">
                {prompt.category}
              </Badge>
            )}
            {isSystemRow && (
              <Badge
                variant="outline"
                className="text-[10px] bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/30"
              >
                System (read-only)
              </Badge>
            )}
            {prompt.clonedFromSystemId && (
              <Badge
                variant="outline"
                className="text-[10px] bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-500/30"
              >
                Cloned
              </Badge>
            )}
          </div>
        </div>
        {!isSystemRow && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setConfirmDelete(true)}
            className="text-destructive hover:bg-destructive/10"
            title="Delete prompt"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Delete
          </Button>
        )}
      </div>

      {/* Provenance banner — when the row was cloned from a system prompt */}
      {prompt.clonedFromSystemId && (
        <Card className="border-cyan-500/30 bg-cyan-500/5">
          <CardContent className="py-3 text-sm flex items-start gap-2">
            <Copy className="h-4 w-4 mt-0.5 shrink-0 text-cyan-700 dark:text-cyan-300" />
            <div>
              <div className="font-medium text-cyan-700 dark:text-cyan-300">
                Cloned from system prompt
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                You can edit this copy freely; the original system prompt is
                unchanged. Provenance UUID:{' '}
                <code className="font-mono">{prompt.clonedFromSystemId}</code>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as DetailTab)} className="space-y-4">
        <TabsList>
          <TabsTrigger value="identity">
            <FileText className="h-3.5 w-3.5 mr-1.5" />
            Identity
          </TabsTrigger>
          <TabsTrigger value="template">
            <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
            Template
          </TabsTrigger>
          <TabsTrigger value="variables">
            <VariableIcon className="h-3.5 w-3.5 mr-1.5" />
            Variables
            {prompt.variables.length > 0 && (
              <Badge variant="secondary" className="ml-1.5 h-4 px-1 text-[9px]">
                {prompt.variables.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="preview">
            <Eye className="h-3.5 w-3.5 mr-1.5" />
            Preview
          </TabsTrigger>
          <TabsTrigger value="used-by">
            <Users className="h-3.5 w-3.5 mr-1.5" />
            Used by
          </TabsTrigger>
        </TabsList>

        <TabsContent value="identity">
          <IdentityTab prompt={prompt} readOnly={isSystemRow} update={updatePrompt} />
        </TabsContent>
        <TabsContent value="template">
          <TemplateTab prompt={prompt} readOnly={isSystemRow} update={updatePrompt} />
        </TabsContent>
        <TabsContent value="variables">
          <VariablesTab prompt={prompt} readOnly={isSystemRow} update={updatePrompt} />
        </TabsContent>
        <TabsContent value="preview">
          <PreviewTab prompt={prompt} />
        </TabsContent>
        <TabsContent value="used-by">
          <UsedByAgentsTab promptCommand={prompt.command} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete prompt</AlertDialogTitle>
            <AlertDialogDescription>
              Delete{' '}
              <code className="bg-muted px-1 rounded">/{prompt.command}</code>?
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete prompt
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Identity tab ──────────────────────────────────────────────────────────

interface PromptUpdateMutation {
  mutateAsync: (input: {
    id: string;
    title?: string;
    description?: string;
    content?: string;
    variables?: PromptVariable[];
    category?: string;
  }) => Promise<unknown>;
  isPending: boolean;
}

function IdentityTab({
  prompt,
  readOnly,
  update,
}: {
  prompt: PromptTemplate;
  readOnly: boolean;
  update: PromptUpdateMutation;
}) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(prompt.title);
  const [description, setDescription] = useState(prompt.description ?? '');
  const [category, setCategory] = useState(prompt.category ?? '');

  useEffect(() => {
    setTitle(prompt.title);
    setDescription(prompt.description ?? '');
    setCategory(prompt.category ?? '');
  }, [prompt.id, prompt.title, prompt.description, prompt.category]);

  const dirty =
    title !== prompt.title ||
    description !== (prompt.description ?? '') ||
    category !== (prompt.category ?? '');

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        id: prompt.id,
        title,
        description: description || undefined,
        category: category || undefined,
      });
      toast.success('Saved changes');
      setEditing(false);
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  const renderChrome = () => {
    if (readOnly) return null;
    if (editing) {
      return (
        <div className="flex gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setTitle(prompt.title);
              setDescription(prompt.description ?? '');
              setCategory(prompt.category ?? '');
              setEditing(false);
            }}
            title="Discard changes"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!dirty || update.isPending}
            title="Save changes"
          >
            {update.isPending ? (
              <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5 mr-1.5" />
            )}
            Save
          </Button>
        </div>
      );
    }
    return (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditing(true)}
        title="Edit"
      >
        <Pencil className="h-3.5 w-3.5 mr-1.5" />
        Edit
      </Button>
    );
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base">Identity</CardTitle>
          <CardDescription>
            Slash command identifier and surface metadata. The command is
            immutable after creation.
          </CardDescription>
        </div>
        {renderChrome()}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-muted-foreground">Command</Label>
            <div className="font-mono text-xs mt-1">/{prompt.command}</div>
          </div>
          <div>
            <Label htmlFor="pe-cat" className="text-xs text-muted-foreground">
              Category
            </Label>
            {editing ? (
              <Input
                id="pe-cat"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="general"
                className="text-xs h-8 mt-1"
              />
            ) : (
              <div className="text-sm mt-1">{category || '—'}</div>
            )}
          </div>
        </div>
        <div>
          <Label htmlFor="pe-title" className="text-xs text-muted-foreground">
            Title
          </Label>
          {editing ? (
            <Input
              id="pe-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="text-sm h-8 mt-1"
            />
          ) : (
            <div className="text-sm mt-1">{prompt.title}</div>
          )}
        </div>
        <div>
          <Label htmlFor="pe-desc" className="text-xs text-muted-foreground">
            Description
          </Label>
          {editing ? (
            <Textarea
              id="pe-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="text-sm mt-1"
            />
          ) : (
            <div className="text-sm mt-1">{description || '—'}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Template tab ──────────────────────────────────────────────────────────

function TemplateTab({
  prompt,
  readOnly,
  update,
}: {
  prompt: PromptTemplate;
  readOnly: boolean;
  update: PromptUpdateMutation;
}) {
  const [editing, setEditing] = useState(false);
  const [content, setContent] = useState(prompt.content);

  useEffect(() => {
    setContent(prompt.content);
  }, [prompt.id, prompt.content]);

  const dirty = content !== prompt.content;

  const handleSave = async () => {
    try {
      await update.mutateAsync({ id: prompt.id, content });
      toast.success('Template saved');
      setEditing(false);
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Template body
          </CardTitle>
          <CardDescription>
            The expanded prompt body. Reference declared variables as{' '}
            <code>{'{{variable_name}}'}</code>. Markdown is preserved when the
            slash command is invoked in chat.
          </CardDescription>
        </div>
        {!readOnly && (
          <div className="flex gap-1.5">
            {editing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setContent(prompt.content);
                    setEditing(false);
                  }}
                  title="Discard changes"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!dirty || update.isPending}
                  title="Save changes"
                >
                  {update.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Save
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(true)}
                title="Edit template"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {editing ? (
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={18}
            className="text-sm font-mono"
          />
        ) : (
          <div className="border rounded-md p-3 max-h-[480px] overflow-y-auto bg-background">
            <pre className="text-xs font-mono whitespace-pre-wrap">
              {prompt.content || '(empty)'}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Variables tab ─────────────────────────────────────────────────────────

function VariablesTab({
  prompt,
  readOnly,
  update,
}: {
  prompt: PromptTemplate;
  readOnly: boolean;
  update: PromptUpdateMutation;
}) {
  const [editing, setEditing] = useState(false);
  const [variables, setVariables] = useState<PromptVariableEntry[]>(
    prompt.variables as PromptVariableEntry[],
  );

  useEffect(() => {
    setVariables(prompt.variables as PromptVariableEntry[]);
  }, [prompt.id, prompt.variables]);

  const dirty =
    JSON.stringify(variables) !==
    JSON.stringify(prompt.variables as PromptVariableEntry[]);
  const hasBlankNames = variablesHaveBlankNames(variables);

  const handleSave = async () => {
    if (hasBlankNames) {
      toast.error('Every variable needs a name before saving.');
      return;
    }
    try {
      await update.mutateAsync({
        id: prompt.id,
        variables: variables as unknown as PromptVariable[],
      });
      toast.success('Variables saved');
      setEditing(false);
    } catch (err) {
      toast.error('Save failed', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between pb-3">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <VariableIcon className="h-4 w-4" />
            Variables ({variables.length})
          </CardTitle>
          <CardDescription>
            Typed inputs the chat surface collects before invoking the prompt.
            Used in the template body via <code>{'{{name}}'}</code> substitution.
          </CardDescription>
        </div>
        {!readOnly && (
          <div className="flex gap-1.5">
            {editing ? (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setVariables(prompt.variables as PromptVariableEntry[]);
                    setEditing(false);
                  }}
                  title="Discard changes"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!dirty || update.isPending || hasBlankNames}
                  title={
                    hasBlankNames
                      ? 'Every variable needs a name'
                      : 'Save changes'
                  }
                >
                  {update.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Save
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditing(true)}
                title="Edit variables"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" />
                Edit
              </Button>
            )}
          </div>
        )}
      </CardHeader>
      <CardContent>
        {editing && !readOnly ? (
          <VariableEditor
            flavor="prompt"
            value={variables}
            onChange={setVariables}
          />
        ) : variables.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center border rounded-md bg-muted/20">
            No variables declared.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Label</TableHead>
                <TableHead className="w-24">Type</TableHead>
                <TableHead className="w-20">Required</TableHead>
                <TableHead>Default</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {variables.map((v, i) => (
                <TableRow key={`${v.name}-${i}`}>
                  <TableCell className="font-mono text-xs">{v.name}</TableCell>
                  <TableCell className="text-xs">{v.label}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">
                      {v.type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs">
                    {v.required ? 'yes' : 'no'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {v.default || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Preview tab ───────────────────────────────────────────────────────────

function PreviewTab({ prompt }: { prompt: PromptTemplate }) {
  const variables = prompt.variables as PromptVariableEntry[];
  const [values, setValues] = useState<Record<string, string>>({});
  const { rendered, missing } = previewPromptTemplate(
    prompt.content,
    variables,
    values,
  );

  return (
    <div className="grid grid-cols-2 gap-4">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <VariableIcon className="h-4 w-4" />
            Variable values
          </CardTitle>
          <CardDescription>
            Fill in values to see the rendered prompt the chat surface would
            send.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {variables.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center border rounded-md bg-muted/20">
              No variables — the template is rendered verbatim.
            </div>
          ) : (
            <VariableValueForm
              variables={variables}
              values={values}
              onChange={setValues}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Eye className="h-4 w-4" />
            Rendered prompt
          </CardTitle>
          <CardDescription>
            Live preview of the substituted template body.
            {missing.length > 0 && (
              <Badge
                variant="outline"
                className="ml-2 text-[9px] bg-amber-500/10 text-amber-700 border-amber-500/30"
              >
                {missing.length} required missing
              </Badge>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded bg-background border p-3 text-xs whitespace-pre-wrap font-mono max-h-[480px] overflow-y-auto">
            {rendered || '(empty)'}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Used by agents tab ────────────────────────────────────────────────────

function UsedByAgentsTab({ promptCommand }: { promptCommand: string }) {
  // Best-effort: agents do not have a first-class prompt-template
  // dependency field today. We scan the system prompt for the slash
  // command (e.g. `/handover-report`) — this catches the explicit
  // references admins typically embed. False positives are possible
  // but rare; false negatives only happen when an admin invokes the
  // prompt dynamically (rare).
  const { data: agents, isLoading, error } = useAgents('all');

  const needle = `/${promptCommand}`;
  const referencingAgents = useMemo(
    () =>
      (agents ?? []).filter((a) => a.systemPrompt.includes(needle)),
    [agents, needle],
  );

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Users className="h-4 w-4" />
          Used by agents ({referencingAgents.length})
        </CardTitle>
        <CardDescription>
          Agents whose system prompt references{' '}
          <code className="bg-muted px-1 rounded">/{promptCommand}</code>.
          Detection is best-effort — dynamic references are not detected.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="py-6 text-center text-sm text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 inline mr-1.5 animate-spin" />
            Loading agents…
          </div>
        ) : error ? (
          <div className="text-sm text-destructive flex items-start gap-2">
            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <div>{error.message}</div>
          </div>
        ) : referencingAgents.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center border rounded-md bg-muted/20">
            No agents detected referencing this prompt.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead className="w-32">Status</TableHead>
                <TableHead className="w-32">Ownership</TableHead>
                <TableHead className="w-20">Version</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {referencingAgents.map((a) => (
                <TableRow key={a.id}>
                  <TableCell>
                    <div className="text-sm font-medium">{a.name}</div>
                    {a.description && (
                      <div className="text-[11px] text-muted-foreground line-clamp-1">
                        {a.description}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] capitalize">
                      {a.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-[10px] capitalize">
                      {a.ownership}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    v{a.version}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
